import { useFormik } from 'formik';
import moment from "moment";
import { FC, useCallback, useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { ActionIcon, Button, Input, Modal, Text, Textarea } from "rizzui";
import { toast } from "sonner";
import * as yup from 'yup';
import WeatherSelectors from "../../../components/commonForm/WeatherSelectors";
import CustomSelect from "../../../components/forms/CustomSelect";
import { DatePicker } from "../../../components/forms/DatePicker";
import FormLabel from "../../../components/forms/FormLabel";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import useStopsStatus from "../../../hooks/api-calls/useStopsStatus";
import useFetch from "../../../hooks/useFetch";
import useHandleErrors from "../../../hooks/useHandleErrors";
import { StopsService } from "../../../services/preventive-stops/stopsService";
import { appSelectors, carrionStopPresentSelector } from "../../../utils/SelectorMapping";
import StopsEntitySelectors from "./StopsEntitySelector";

const timeToMinutes = (time?: string | null): number | null => {
    if (!time) return null;
    const [hours, minutes] = time.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    return hours * 60 + minutes;
};

const TIME_FIELDS = ['startTime', 'stopTime', 'resumeOrderTime', 'resumeTime'] as const;

interface StopsFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    refetch: () => void;
    stop: string | null;
    isFromPark?: boolean;
    parkId?: string;
}

const schema = yup.object({
    parkId: yup.string().required("Campo requerido"),
    userId: yup.string().required("Campo requerido"),
    windTurbines: yup.array().min(1, "Al menos debes seleccionar una turbina"),
    species: yup.array().min(1, "Al menos debes seleccionar una especie"),
    date: yup.string().required("Campo requerido"),
    startTime: yup.string().required("Campo requerido")
        .test('startTime-before-stopTime', 'Hora de llamada debe ser menor o igual que hora parada', function (value) {
            const { stopTime } = this.parent;
            const startMinutes = timeToMinutes(value);
            const stopMinutes = timeToMinutes(stopTime);
            if (startMinutes === null || stopMinutes === null) return true;
            return startMinutes <= stopMinutes;
        }),
    stopTime: yup.string().required("Campo requerido")
        .test('stopTime-after-startTime', 'Hora parada debe ser mayor o igual que hora llamada', function (value) {
            const { startTime } = this.parent;
            const startMinutes = timeToMinutes(startTime);
            const stopMinutes = timeToMinutes(value);
            if (startMinutes === null || stopMinutes === null) return true;
            return stopMinutes >= startMinutes;
        })
        .test('stopTime-before-resumeOrderTime', 'Hora parada debe ser menor que hora fin', function (value) {
            const { resumeOrderTime } = this.parent;
            const stopMinutes = timeToMinutes(value);
            const resumeOrderMinutes = timeToMinutes(resumeOrderTime);
            if (stopMinutes === null || resumeOrderMinutes === null) return true;
            return stopMinutes < resumeOrderMinutes;
        }),
    resumeOrderTime: yup.string().required("Campo requerido")
        .test('resumeOrderTime-before-resumeTime', 'Hora fin debe ser menor o igual que Hora Arranque', function (value) {
            const { resumeTime } = this.parent;
            const resumeOrderMinutes = timeToMinutes(value);
            const resumeMinutes = timeToMinutes(resumeTime);
            if (resumeOrderMinutes === null || resumeMinutes === null) return true;
            return resumeOrderMinutes <= resumeMinutes;
        }),
    resumeTime: yup.string().required("Campo requerido")
        .test('resumeTime-after-resumeOrderTime', 'Hora arranque debe ser mayor o igual que hora fin', function (value) {
            const { resumeOrderTime } = this.parent;
            const resumeOrderMinutes = timeToMinutes(resumeOrderTime);
            const resumeMinutes = timeToMinutes(value);
            if (resumeOrderMinutes === null || resumeMinutes === null) return true;
            return resumeMinutes >= resumeOrderMinutes;
        })
        .test('resumeTime-after-stopTime', 'Hora arranque debe ser mayor que hora parada', function (value) {
            const { stopTime } = this.parent;
            const stopMinutes = timeToMinutes(stopTime);
            const resumeMinutes = timeToMinutes(value);
            if (stopMinutes === null || resumeMinutes === null) return true;
            return resumeMinutes > stopMinutes;
        }),
    downtimePerMachine: yup.mixed().required("Campo requerido")
        .test('positive-downtime', 'No pueden existir paradas con tiempo negativo o cero', function (value) {
            const { stopTime, resumeTime } = this.parent;
            const stopMinutes = timeToMinutes(stopTime);
            const resumeMinutes = timeToMinutes(resumeTime);
            if (stopMinutes === null || resumeMinutes === null) return true;
            if (value === '' || value === null || value === undefined) return false;
            return Number(value) > 0;
        }),
    numMachines: yup.string().required("Campo requerido"),
    totalDowntime: yup.number().required("Campo requerido"),
    cause: yup.string().required("Campo requerido"),
    comment: yup.string().nullable(),
    windDirection: yup.string().nullable(),
    windSpeed: yup.mixed().nullable()
        .test('no-calm-wind', 'La intensidad de viento no puede ser calma en una parada', (value) => {
            if (value === null || value === undefined || value === '') return true;
            return Number(value) !== 0;
        }),
    cloudCoverage: yup.string().nullable(),
    stopWithApp: yup.string().required("Campo requerido"),
    restartWithApp: yup.string().required("Campo requerido"),
    appIssue: yup.string().nullable().test('required', 'Campo requerido', function (value) {
        const { stopWithApp, restartWithApp } = this.parent;
        if (
            stopWithApp === 'no' || restartWithApp === 'no'
            || stopWithApp === 'partial' || restartWithApp === 'partial'
        ) {
            return !!value && value.trim() !== '';
        }
        return true;
    }),
    carrionPresent: yup.string().required("Campo requerido"),
    additionalComments: yup.string().nullable(),
});

interface FormValues {
    parkId: string;
    userId: string;
    windTurbines: string[];
    species: string[];
    statusId: string;
    date: string;
    startTime: string;
    stopTime: string;
    resumeOrderTime: string;
    resumeTime: string;
    downtimePerMachine: string;
    numMachines: string;
    totalDowntime: string;
    cause: string;
    comment: string;
    windDirection: string | null;
    windSpeed: number | null;
    cloudCoverage: string | null;
    stopWithApp: string;
    restartWithApp: string | null;
    appIssue: string | null;
    carrionPresent: string;
    additionalComments: string | null;
    company: string | null;
    photos?: File[];
}

const StopsFormModal: FC<StopsFormModalProps> = ({ isOpen, onClose, refetch, stop, isFromPark, parkId }) => {

    // STATES

    const service = new StopsService();
    const isEdit = stop !== null;
    const [speciesWithQuantities, setSpeciesWithQuantities] = useState<any[]>([]);
    const [lastGeneratedObservations, setLastGeneratedObservations] = useState("");

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { getStopsStatusList } = useStopsStatus();
    const { userCan } = usePrivilege();

    // DATA FETCHING

    const [data, loading, error] = useFetch(
        useCallback(async () => {
            if (!stop) return;
            const response = await service.getStopsById(stop);
            return response.getResponseData();
        }, [stop])
    );

    // FORMIK

    const formik = useFormik<FormValues>({
        initialValues: {
            parkId: parkId ? parkId : "",
            userId: "",
            windTurbines: [],
            species: [],
            statusId: "",
            date: "",
            startTime: "",
            stopTime: "",
            resumeOrderTime: "",
            resumeTime: "",
            downtimePerMachine: "",
            numMachines: "",
            totalDowntime: "",
            cause: "",
            comment: "",
            windDirection: null,
            windSpeed: null,
            cloudCoverage: null,
            stopWithApp: "",
            restartWithApp: null,
            appIssue: null,
            carrionPresent: "",
            additionalComments: null,
            company: null,
        },
        validationSchema: schema,
        validateOnBlur: false,
        onSubmit: values => {
            if (!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    // FUNCTIONS

    const showFieldError = (field: keyof FormValues) => {
        const error = formik.errors[field];
        const touched = formik.touched[field];
        if (!error || typeof error !== 'string') return undefined;
        return touched || formik.submitCount > 0 ? error : undefined;
    };

    const touchTimeFields = () => {
        const touched = TIME_FIELDS.reduce<Record<string, boolean>>((acc, field) => {
            acc[field] = true;
            return acc;
        }, {});
        formik.setTouched({ ...formik.touched, ...touched });
    };

    const handleTimeChange = (field: typeof TIME_FIELDS[number], date: Date | null) => {
        const formattedDate = date && moment(date).isValid() ? moment(date).format('HH:mm') : '';
        formik.setFieldValue(field, formattedDate);
        formik.setFieldTouched(field, true, false);
        touchTimeFields();
        setTimeout(() => formik.validateForm(), 0);
    };

    const handleSubmitClick = () => {
        touchTimeFields();
        formik.setFieldTouched('downtimePerMachine', true, false);
        formik.handleSubmit();
    };

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES GENERA EL TEXTO DE OBSERVACIONES AUTOMATICAMENTE
     * @EN GENERATES OBSERVATIONS TEXT AUTOMATICALLY
     * 
     * @param species - Array de especies con cantidades
     * @param comment - Comentarios principales del usuario
     */
    //-----------------------------------------------------------------------------------------------------------------
    const generateObservationsText = (species: any[], comment: string | null): string => {
        if (!species || species.length === 0) {
            return comment || '';
        }

        // Obtener los nombres de las especies desde speciesWithQuantities
        const speciesTexts = species.map((specie: any) => {
            const speciesInfo = speciesWithQuantities.find((s: any) => s.value === specie.id || s.id === specie.id);
            const name = speciesInfo?.scientificName || speciesInfo?.listLabel || speciesInfo?.commonName || specie.commonName || 'Especie desconocida';
            return name + ' (' + specie.numIndividuals + ')';
        });

        let observationsText = '';

        if (species.length === 1) {
            observationsText = speciesTexts[0];
        } else if (species.length === 2) {
            observationsText = `${speciesTexts[0]} y ${speciesTexts[1]}`;
        } else {
            // Para más de 2 especies: "X ejemplares, Y ejemplares y Z ejemplares"
            const lastSpecie = speciesTexts.pop();
            observationsText = `${speciesTexts.join(', ')} y ${lastSpecie}`;
        }

        // Si hay comentarios principales, agregarlos después de un punto
        if (comment && comment.trim() !== '') {
            observationsText += `. ${comment.trim()}`;
        }

        return observationsText;
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES GENERA EL TEXTO DE CAUSA AUTOMATICAMENTE
     * @EN GENERATES CAUSE TEXT AUTOMATICALLY
     * 
     * @param species - Array de especies con cantidades
     * @param carrionPresent - Presencia de carroña
     */
    //-----------------------------------------------------------------------------------------------------------------
    const generateCauseText = (species: any[], carrionPresent: string): string => {
        let causeText = '';

        if (species.length > 0) {

            const sortedSpecies = [...species].sort((a, b) => (b.numIndividuals || 0) - (a.numIndividuals || 0));
            if (sortedSpecies.length === 1) {
                const singleSpecies = sortedSpecies[0];
                const count = singleSpecies.numIndividuals || 0;
                const speciesInfo = speciesWithQuantities.find((s: any) => s.commonName === singleSpecies.commonName);

                const commonName = speciesInfo?.commonName || 'Especie desconocida';
            
                if (count === 1 || count === '1') {
                    causeText = commonName;
                } else {
                    causeText = `Bando de ${commonName}`;
                }
            } else {
                const speciesNames = sortedSpecies.map(s => {
                    const speciesInfo = speciesWithQuantities.find((sp: any) => sp.commonName === s.commonName);
                    return speciesInfo?.commonName || 'Especie desconocida';
                });
                causeText = `Bando mixto de ${speciesNames.join(', ')}`;
            }
        }

        if (carrionPresent === 'yes') {
            causeText += '. Si, hay Carroña dentro del parque.';
        } else if (carrionPresent === 'yes_out') {
            causeText += '. Si, hay Carroña fuera del parque.';
        }
        
        return causeText;
    };
    //-----------------------------------------------------------------------------------------------------------------

    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES MANEJA LA CREACION DE UNA NUEVA PARADA
     * @EN HANDLES THE CREATION OF A NEW STOP
     * 
     * @param values
     */
    //-----------------------------------------------------------------------------------------------------------------
    const handleCreate = async (values: any) => {
        try {
            const formData = new FormData();

            Object.entries(values).forEach(([key, value]: any) => {
                if (value !== null && value !== "" && !['photos', 'species', 'windTurbines'].includes(key)) {
                    // Convert date from DD/MM/YYYY to YYYY-MM-DD for backend
                    if (key === 'date') {
                        const formattedDate = moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
                        formData.append(key, formattedDate);
                    } else {
                        formData.append(key, value);
                    }
                }

                if (key === 'species') {
                    values.species.forEach((specie: any, key: any) => {
                        formData.append(`species[${key}][id]`, specie.id);
                        formData.append(`species[${key}][numIndividuals]`, specie.numIndividuals);
                    });
                }

                if (key === 'windTurbines') {
                    values.windTurbines.forEach((turbine: any) => {
                        formData.append('windTurbineIds[]', turbine);
                    });
                }
            });

            if (values.photos) {
                for (let i = 0; i < values.photos.length; i++) {
                    formData.append(`photos[${i}]`, values.photos[i]);
                }
            }

            const response = await (await service.createStop(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Parada creada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-----------------------------------------------------------------------------------------------------------------


    //-----------------------------------------------------------------------------------------------------------------
    /**
     * @ES EDITAR UNA PARADA
     * @EN EDIT A STOP
     * 
     * @param values
     */
    //----------------------------------------------------------------------------------------------------------------
    const handleEdit = async (values: any) => {
        try {
            const formData = new FormData();

            Object.entries(values).forEach(([key, value]: any) => {
                if (value !== null && value !== "" && !['photos', 'species', 'windTurbines'].includes(key)) {
                    // Convert date from DD/MM/YYYY to YYYY-MM-DD for backend
                    if (key === 'date') {
                        const formattedDate = moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD');
                        formData.append(key, formattedDate);
                    } else {
                        formData.append(key, value);
                    }
                }

                if (key === 'species') {
                    values.species.forEach((specie: any, key: any) => {
                        formData.append(`species[${key}][id]`, specie.id);
                        formData.append(`species[${key}][numIndividuals]`, specie.numIndividuals);
                    });
                }

                if (key === 'windTurbines') {
                    values.windTurbines.forEach((turbine: any) => {
                        formData.append('windTurbineIds[]', turbine);
                    });
                }
            });

            if (values.photos) {
                for (let i = 0; i < values.photos.length; i++) {
                    formData.append(`photos[${i}]`, values.photos[i]);
                }
            }

            const response = await (await service.editStop(formData)).getResponseData();
            if (response.success) {
                refetch();
                onClose();
                formik.resetForm();
                toast.success('Parada editada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };
    //-------------------------------------------------------------------------------------------------------------


    // USE EFFECTS

    useEffect(() => {
        if (isEdit) {
            formik.setFieldValue('id', data?.id);
            formik.setFieldValue('parkId', data?.park.id);
            formik.setFieldValue('userId', data?.user.id);
            formik.setFieldValue('windTurbines', data?.windTurbinesPreventiveStops.map((turbine: any) => turbine.windTurbine.id));
            formik.setFieldValue('statusId', data?.preventiveStopsStatus?.id);
            formik.setFieldValue('date', moment(data?.date?.date).format('DD/MM/YYYY'));
            formik.setFieldValue('startTime', moment(data?.startTime?.date).format('HH:mm'));
            formik.setFieldValue('stopTime', moment(data?.stopTime?.date).format('HH:mm'));
            formik.setFieldValue('resumeOrderTime', data?.resumeOrderTime ? moment(data?.resumeOrderTime?.date).format('HH:mm') : null);
            formik.setFieldValue('resumeTime', moment(data?.resumeTime?.date).format('HH:mm'));
            formik.setFieldValue('downtimePerMachine', data?.downtimePerMachine);
            formik.setFieldValue('numMachines', data?.numMachines);
            formik.setFieldValue('totalDowntime', data?.totalDowntime);
            formik.setFieldValue('cause', data?.cause);
            formik.setFieldValue('comment', data?.comments);
            formik.setFieldValue('windDirection', data?.windDirection);
            formik.setFieldValue('windSpeed', data?.windSpeed);
            formik.setFieldValue('cloudCoverage', data?.cloudCoverage);
            formik.setFieldValue('stopWithApp', data?.stopWithApp);
            formik.setFieldValue('restartWithApp', data?.restartWithApp);
            formik.setFieldValue('appIssue', data?.appIssue);
            formik.setFieldValue('carrionPresent', data?.carrionPresence);
            formik.setFieldValue('additionalComments', data?.additionalComments);
            setSpeciesWithQuantities(data?.speciesPreventiveStops.map((species: any) => ({
                id: species.species.id,
                numIndividuals: species.numIndividuals,
                label: `${species.species.commonName} (${species.numIndividuals})`,
                listLabel: species.species.commonName,
            })));
        } else {
            formik.resetForm();
            setSpeciesWithQuantities([]);
        }
    }, [data]);

    useEffect(() => {
        const stopMinutes = timeToMinutes(formik.values.stopTime);
        const resumeMinutes = timeToMinutes(formik.values.resumeTime);
        if (stopMinutes !== null && resumeMinutes !== null) {
            const downtime = resumeMinutes - stopMinutes;
            formik.setFieldValue('downtimePerMachine', downtime);
        }
    }, [formik.values.stopTime, formik.values.resumeTime]);

    useEffect(() => {
        if (formik.values.downtimePerMachine && formik.values.numMachines) {
            const totalDowntime = parseInt(formik.values.downtimePerMachine) * parseInt(formik.values.numMachines);
            formik.setFieldValue('totalDowntime', totalDowntime);
        } else if (formik.values.downtimePerMachine == '0') {
            formik.setFieldValue('totalDowntime', 0);
        }
    }, [formik.values.downtimePerMachine, formik.values.numMachines]);

    // Efecto para generar automáticamente la causa basada en especies y carroña
    useEffect(() => {
        const hasSpecies = formik.values.species && formik.values.species.length > 0 && speciesWithQuantities.length > 0;
        const hasCarrionInfo = formik.values.carrionPresent === 'yes' || formik.values.carrionPresent === 'yes_out';

        if (hasSpecies || hasCarrionInfo) {
            const generatedCause = generateCauseText(formik.values.species, formik.values.carrionPresent);
            if (generatedCause && generatedCause !== formik.values.cause) {
                formik.setFieldValue('cause', generatedCause);
            }
        }
    }, [formik.values.species, formik.values.carrionPresent, speciesWithQuantities]);

    // Efecto para generar automáticamente las observaciones basadas en especies
    useEffect(() => {
        const newGenerated = (formik.values.species && formik.values.species.length > 0 && speciesWithQuantities.length > 0) 
            ? generateObservationsText(formik.values.species, null) 
            : "";
            
        if (newGenerated !== lastGeneratedObservations) {
            let currentComment = formik.values.comment || "";
            
            if (lastGeneratedObservations && currentComment.startsWith(lastGeneratedObservations)) {
                currentComment = currentComment.replace(lastGeneratedObservations, newGenerated);
                // Limpiar puntos, comas o espacios extra al principio si se borraron especies
                currentComment = currentComment.replace(/^[\s.,]+/, '');
            } else if (!currentComment) {
                currentComment = newGenerated;
            } else if (newGenerated) {
                currentComment = `${newGenerated}. ${currentComment}`;
            }
            
            formik.setFieldValue('comment', currentComment);
            setLastGeneratedObservations(newGenerated);
        }
    }, [formik.values.species, speciesWithQuantities]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                formik.resetForm();
                setSpeciesWithQuantities([]);
                onClose();
            }}
            size='xl'
            overlayClassName='backdrop-blur'
            containerClassName='!max-w-[800px] !shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">{isEdit ? 'Editar ' : 'Crear '} Parada Preventiva</Text>
                    <ActionIcon size='sm' variant='text' onClick={() => {
                        formik.resetForm();
                        setSpeciesWithQuantities([]);
                        onClose();
                    }}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <StopsEntitySelectors formik={formik} isFromPark={isFromPark} setSpeciesWithQuantities={setSpeciesWithQuantities} speciesWithQuantities={speciesWithQuantities} />

                <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-4 mt-3">

                    {isEdit && (
                        <div className={`${userCan('list', 'companies') ? '' : 'col-span-2'}`}>
                            <CustomSelect
                                isSearchable
                                id={'statusId'}
                                label="Estado"
                                options={getStopsStatusList()}
                                onChange={(e: any) => {
                                    formik.setFieldValue('statusId', e.value)
                                }}
                                value={
                                    getStopsStatusList().filter((status: any) => status.value === formik.values.statusId)
                                }
                                required
                            />
                        </div>
                    )}

                    {userCan('list', 'companies') ? (
                        <CustomSelect
                            isSearchable
                            id={'company'}
                            label="Compañia"
                            options={getCompaniesList()}
                            onChange={(e: any) => {
                                formik.setFieldValue('company', e.value)
                            }}
                            value={
                                getCompaniesList().filter((company: any) => company.value === formik.values.company)
                            }
                            className="mb-4"
                        />
                    ) : null}

                </div>

                <WeatherSelectors formik={formik} required={false} excludeCalmIntensity />


                <div className="grid grid-cols-1 gap-2 mb-3 mt-3">
                    <CustomSelect
                        isSearchable
                        id={'carrionPresent'}
                        label="Hay carroña"
                        options={carrionStopPresentSelector}
                        onChange={(e: any) => {
                            formik.setFieldValue('carrionPresent', e.value)
                        }}
                        value={
                            formik.values.carrionPresent
                                ? carrionStopPresentSelector.find(option => option.value == formik.values.carrionPresent)
                                : null
                        }
                        required
                        error={formik.errors.carrionPresent && formik.touched.carrionPresent ? formik.errors.carrionPresent : undefined}
                    />
                </div>

                <div className="grid md:grid-cols-3 grid-cols-1 gap-2 mb-4 mt-3">

                    <div className="flex flex-col">
                        <FormLabel label="Fecha" required>
                            <DatePicker
                                className={formik.errors.date && formik.touched.date ? 'invalid-form-input w-full' : 'w-full'}
                                placeholderText="Selecciona la fecha"
                                selected={formik.values.date ? moment(formik.values.date, 'DD/MM/YYYY').toDate() : null}
                                onChange={(date: any) => {
                                    const formattedDate = moment(date).format('DD/MM/YYYY');
                                    formik.setFieldValue('date', formattedDate);
                                }}
                                dateFormat="d/MM/yyyy"
                                required
                            />
                        </FormLabel>
                        {formik.errors.date && formik.touched.date && (
                            <Text className="text-red-500 text-sm mt-1">
                                {/* @ts-ignore */}
                                {formik.errors.date}
                            </Text>
                        )}
                    </div>

                    <FormLabel label="Hora llamada" required>
                        <DatePicker
                            className={showFieldError('startTime') ? 'invalid-form-input w-full' : 'w-full'}
                            placeholderText="Selecciona la hora"
                            selected={formik.values.startTime ? moment(formik.values.startTime, 'HH:mm').toDate() : null}
                            onChange={(date: any) => handleTimeChange('startTime', date)}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={5}
                            timeCaption="Hora"
                            timeFormat="HH:mm"
                            required
                        />
                        {showFieldError('startTime') && (
                            <Text className="text-red-500 text-sm mt-1">
                                {showFieldError('startTime')}
                            </Text>
                        )}
                    </FormLabel>

                    <FormLabel label="Hora parada" required>
                        <DatePicker
                            className={showFieldError('stopTime') ? 'invalid-form-input w-full' : 'w-full'}
                            minTime={formik.values.startTime ? moment(formik.values.startTime, 'HH:mm').toDate() : moment('00:00', 'HH:mm').toDate()}
                            maxTime={moment('23:59', 'HH:mm').toDate()}
                            placeholderText="Selecciona la hora"
                            selected={formik.values.stopTime ? moment(formik.values.stopTime, 'HH:mm').toDate() : null}
                            onChange={(date: any) => handleTimeChange('stopTime', date)}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={5}
                            timeCaption="Hora"
                            timeFormat="HH:mm"
                            required
                        />
                        {showFieldError('stopTime') && (
                            <Text className="text-red-500 text-sm mt-1">
                                {showFieldError('stopTime')}
                            </Text>
                        )}
                    </FormLabel>

                </div>

                <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-4">
                    <FormLabel label="Hora fin" required>
                        <DatePicker
                            className={showFieldError('resumeOrderTime') ? 'invalid-form-input w-full' : 'w-full'}
                            placeholderText="Selecciona la hora"
                            selected={formik.values.resumeOrderTime ? moment(formik.values.resumeOrderTime, 'HH:mm').toDate() : null}
                            onChange={(date: any) => handleTimeChange('resumeOrderTime', date)}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={5}
                            timeCaption="Hora"
                            timeFormat="HH:mm"
                            required
                        />
                        {showFieldError('resumeOrderTime') && (
                            <Text className="text-red-500 text-sm mt-1">
                                {showFieldError('resumeOrderTime')}
                            </Text>
                        )}
                    </FormLabel>

                    <FormLabel label="Hora Arranque" required>
                        <DatePicker
                            className={showFieldError('resumeTime') ? 'invalid-form-input w-full' : 'w-full'}
                            placeholderText="Selecciona la hora"
                            selected={formik.values.resumeTime ? moment(formik.values.resumeTime, 'HH:mm').toDate() : null}
                            onChange={(date: any) => handleTimeChange('resumeTime', date)}
                            dateFormat="HH:mm"
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={5}
                            timeCaption="Hora"
                            timeFormat="HH:mm"
                            required
                        />
                        {showFieldError('resumeTime') && (
                            <Text className="text-red-500 text-sm mt-1">
                                {showFieldError('resumeTime')}
                            </Text>
                        )}
                    </FormLabel>
                </div>

                {(showFieldError('startTime') || showFieldError('stopTime') || showFieldError('resumeOrderTime') || showFieldError('resumeTime')) && (
                    <Text className="text-red-500 text-sm mb-2 px-1">
                        El orden debe ser: hora llamada ≤ hora parada &lt; hora fin ≤ hora arranque
                    </Text>
                )}

                <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-2 border border-primary-200 rounded-lg p-4">
                    <div className="flex flex-col">
                        <FormLabel label="Tiempo de inactividad por máquina">
                            <Input
                                className={showFieldError('downtimePerMachine') ? 'invalid-form-input w-full' : 'w-full'}
                                type="text"
                                disabled
                                placeholder="Minutos de inactividad por máquina"
                                value={(formik.values.downtimePerMachine !== '' && formik.values.downtimePerMachine !== null && formik.values.downtimePerMachine !== undefined
                                    ? formik.values.downtimePerMachine
                                    : '--') + ' minutos'}
                                key={formik.values.downtimePerMachine}
                            />
                        </FormLabel>
                        {showFieldError('downtimePerMachine') && (
                            <Text className="text-red-500 text-sm mt-1">
                                {showFieldError('downtimePerMachine')}
                            </Text>
                        )}
                    </div>
                    <FormLabel label="Minutos totales de inactividad">
                        <Input
                            className={'w-full'}
                            type="text"
                            disabled
                            placeholder="Tiempo total de inactividad"
                            value={(formik.values.totalDowntime || '--') + ' minutos'}
                            key={formik.values.totalDowntime}
                        />
                    </FormLabel>
                </div>
            </div>

            <div className="flex flex-col mb-4 px-6">
                <FormLabel label="Causa" required>
                    <Textarea
                        id='cause'
                        rows={6}
                        required
                        value={formik.values.cause}
                        onChange={(e) => formik.setFieldValue('cause', e.target.value)}
                        className={formik.errors.cause && formik.touched.cause ? 'invalid-form-input' : ''}
                    />
                </FormLabel>
                {formik.errors.cause && formik.touched.cause && (
                    <Text className="text-red-500 text-sm mt-1">
                        {formik.errors.cause}
                    </Text>
                )}
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-2 px-6">
                <div className="flex flex-col">
                    <FormLabel label="Observaciones" required>
                        <Textarea
                            id='comment'
                            rows={6}
                            required
                            value={formik.values.comment || ''}
                            onChange={(e) => formik.setFieldValue('comment', e.target.value)}
                            className={formik.errors.comment && formik.touched.comment ? 'invalid-form-input' : ''}

                        />
                    </FormLabel>
                    {formik.errors.comment && formik.touched.comment && (
                        <Text className="text-red-500 text-sm mt-1">
                            {formik.errors.comment}
                        </Text>
                    )}
                </div>

                <Textarea
                    id='additionalComments'
                    label='Observaciones adicionales'
                    rows={6}
                    value={formik.values.additionalComments || ''}
                    onChange={(e) => formik.setFieldValue('additionalComments', e.target.value)}
                    className="mb-4"
                />
            </div>

            <div className="grid md:grid-cols-2 grid-cols-1 gap-2 mb-2 px-6">

                <CustomSelect
                    isSearchable
                    id={'stopWithApp'}
                    label="Parada con app"
                    options={appSelectors}
                    onChange={(e: any) => {
                        formik.setFieldValue('stopWithApp', e.value)
                    }}
                    value={
                        formik.values.stopWithApp
                            ? appSelectors.find(option => option.value == formik.values.stopWithApp)
                            : null
                    }
                    required
                    error={formik.errors.stopWithApp && formik.touched.stopWithApp ? formik.errors.stopWithApp : undefined}
                />

                <CustomSelect
                    isSearchable
                    id={'restartWithApp'}
                    label="Arrancado con la app"
                    options={appSelectors}
                    onChange={(e: any) => {
                        formik.setFieldValue('restartWithApp', e.value)
                    }}
                    value={
                        formik.values.restartWithApp
                            ? appSelectors.find(option => option.value == formik.values.restartWithApp)
                            : null
                    }
                    required
                    error={formik.errors.restartWithApp && formik.touched.restartWithApp ? formik.errors.restartWithApp : undefined}
                />

            </div>

            <div className="flex flex-col mb-4 px-6">
                <FormLabel label="Incidencias con la app" required={['no', 'partial'].some(item => item === formik.values.stopWithApp || item === formik.values.restartWithApp)}>
                    <Textarea
                        id='appIssue'
                        rows={6}
                        required={['no', 'partial'].some(item => item === formik.values.stopWithApp || item === formik.values.restartWithApp)}
                        value={formik.values.appIssue || ''}
                        onChange={(e) => formik.setFieldValue('appIssue', e.target.value)}
                        className={formik.errors.appIssue && formik.touched.appIssue ? 'invalid-form-input' : ''}
                    />
                </FormLabel>
                {formik.errors.appIssue && formik.touched.appIssue && (
                    <Text className="text-red-500 text-sm mt-1">
                        {formik.errors.appIssue}
                    </Text>
                )}
            </div>

            {/* {isEdit ? null : <div className='mb-4 mt-2 px-6'>
                <FormLabel label="Fotos de la parada">
                    <input
                        type="file"
                        id="photos"
                        name="photos"
                        accept="image/*"
                        multiple
                        onChange={(event) => {
                            const files = event?.currentTarget?.files ? event.currentTarget.files : null;
                            formik.setFieldValue("photos", files);
                        }}
                        className='mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700'
                    />
                    {formik.errors.photos && formik.touched.photos && (
                        <Text className="text-red-500 text-sm mt-1">
                            {formik.errors.photos}
                        </Text>
                    )}
                </FormLabel>
            </div>} */}

            <div className='flex justify-center p-7'>
                <Button
                    disabled={false}
                    onClick={handleSubmitClick}
                    color='primary'
                >
                    {isEdit ? 'Editar' : 'Crear'}
                </Button>
            </div>
        </Modal>
    )
}
export default StopsFormModal;